'use client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { walletApi } from '../api/wallet.api';

export const WALLET_QUERY_KEYS = {
  all: ['wallet'] as const,
  supportedAssets: () => [...WALLET_QUERY_KEYS.all, 'supported-assets'] as const,
  supportedCountries: () => [...WALLET_QUERY_KEYS.all, 'supported-countries'] as const,
  paymentTypes: (fiat: string, country: string, crypto: string) =>
    [...WALLET_QUERY_KEYS.all, 'payment-types', { fiat, country, crypto }] as const,
  quotes: (fiat: string, crypto: string, amount: number, country: string, pm: string) =>
    [...WALLET_QUERY_KEYS.all, 'quotes', { fiat, crypto, amount, country, pm }] as const,
};

export const useSupportedAssets = () => {
  return useQuery({
    queryKey: WALLET_QUERY_KEYS.supportedAssets(),
    queryFn: () => walletApi.getSupportedAssets(),
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
  });
};

export const useSupportedCountries = () => {
  return useQuery({
    queryKey: WALLET_QUERY_KEYS.supportedCountries(),
    queryFn: () => walletApi.getSupportedCountries(),
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
  });
};

export const usePaymentTypes = (fiat: string, country: string, crypto: string) => {
  return useQuery({
    queryKey: WALLET_QUERY_KEYS.paymentTypes(fiat, country, crypto),
    queryFn: () => walletApi.getPaymentTypes(fiat, country, crypto),
    enabled: !!fiat && !!country && !!crypto,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
};

export const useQuotes = (
  fiat: string,
  crypto: string,
  amount: number,
  country: string,
  pm: string
) => {
  return useQuery({
    queryKey: WALLET_QUERY_KEYS.quotes(fiat, crypto, amount, country, pm),
    queryFn: () => walletApi.getQuotes(fiat, crypto, amount, country, pm),
    enabled: !!fiat && !!crypto && !!amount && !!country && !!pm,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
  });
};

export const useInitiateDeposit = () => {
  return useMutation({
    mutationFn: walletApi.initiateDeposit,
  });
};

export const useLinkWallet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: walletApi.linkWallet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.all });
    },
  });
};
